import { supabase } from "@/integrations/supabase/client";
import { emitMapsLifecycle } from "@/lib/maps-lifecycle-bus";

/**
 * Google Maps script loader with retry/backoff and diagnostics.
 *
 * Diagnostics are attached to `window.__mapsDiagnostics` and emitted via
 * `console.debug` under the `[google-maps-loader]` prefix so they can be
 * inspected in any environment (dev or production).
 */

let loadPromise: Promise<any> | null = null;

const MAX_ATTEMPTS = 4;
const BASE_DELAY_MS = 400;
const MAX_DELAY_MS = 5000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function backoffDelay(attempt: number): number {
  const exp = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt);
  return Math.round(Math.random() * exp);
}

// ---------- Diagnostics ----------

export interface AttemptRecord {
  attempt: number;
  durationMs: number;
  ok: boolean;
  error?: string;
}

export interface MapsDiagnostics {
  startedAt: string;
  finishedAt?: string;
  totalMs?: number;
  status: "pending" | "success" | "error";
  configEndpoint: {
    attempts: AttemptRecord[];
    finalStatus: "ok" | "error" | "pending";
    totalMs?: number;
  };
  scriptInjection: {
    attempts: AttemptRecord[];
    finalStatus: "ok" | "error" | "pending" | "skipped";
    totalMs?: number;
    scriptUrl?: string;
  };
  error?: string;
}

const NS = "[google-maps-loader]";

function newDiagnostics(): MapsDiagnostics {
  return {
    startedAt: new Date().toISOString(),
    status: "pending",
    configEndpoint: { attempts: [], finalStatus: "pending" },
    scriptInjection: { attempts: [], finalStatus: "pending" },
  };
}

function publishDiagnostics(d: MapsDiagnostics) {
  if (typeof window !== "undefined") {
    (window as any).__mapsDiagnostics = d;
  }
}

export function getMapsDiagnostics(): MapsDiagnostics | null {
  if (typeof window === "undefined") return null;
  return (window as any).__mapsDiagnostics ?? null;
}

// ---------- Retry helper with per-attempt timing ----------

async function withRetry<T>(
  label: string,
  record: { attempts: AttemptRecord[] },
  fn: () => Promise<T>,
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const t0 = performance.now();
    try {
      const result = await fn();
      const durationMs = Math.round(performance.now() - t0);
      record.attempts.push({ attempt, durationMs, ok: true });
      console.debug(`${NS} ${label} succeeded on attempt ${attempt} in ${durationMs}ms`);
      return result;
    } catch (err) {
      const durationMs = Math.round(performance.now() - t0);
      const message = err instanceof Error ? err.message : String(err);
      record.attempts.push({ attempt, durationMs, ok: false, error: message });
      lastErr = err;
      if (attempt === MAX_ATTEMPTS) break;
      const delay = backoffDelay(attempt - 1);
      console.warn(
        `${NS} ${label} attempt ${attempt} failed in ${durationMs}ms — retrying in ${delay}ms`,
        err,
      );
      await sleep(delay);
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(`Failed to ${label}`);
}

// ---------- Phase 1: fetch API key from protected endpoint ----------

async function fetchApiKey(d: MapsDiagnostics): Promise<string> {
  const t0 = performance.now();
  try {
    const apiKey = await withRetry("fetch maps config", d.configEndpoint, async () => {
      const { data, error } = await supabase.functions.invoke("get-maps-config");
      if (error) throw error;
      if (!data?.apiKey) throw new Error("Missing API key in response");
      return data.apiKey as string;
    });
    d.configEndpoint.finalStatus = "ok";
    d.configEndpoint.totalMs = Math.round(performance.now() - t0);
    publishDiagnostics(d);
    return apiKey;
  } catch (err) {
    d.configEndpoint.finalStatus = "error";
    d.configEndpoint.totalMs = Math.round(performance.now() - t0);
    publishDiagnostics(d);
    throw err;
  }
}

// ---------- Phase 2: inject the Maps JS script ----------

function injectScript(apiKey: string, d: MapsDiagnostics): Promise<any> {
  const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
  d.scriptInjection.scriptUrl = scriptUrl.replace(/key=[^&]+/, "key=***");
  publishDiagnostics(d);

  const t0 = performance.now();
  return withRetry("inject maps script", d.scriptInjection, () => {
    return new Promise<any>((resolve, reject) => {
      const existing = document.getElementById(
        "google-maps-script",
      ) as HTMLScriptElement | null;
      if (existing) {
        if ((window as any).google?.maps?.places) {
          resolve((window as any).google);
          return;
        }
        existing.remove();
      }

      const script = document.createElement("script");
      script.id = "google-maps-script";
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if ((window as any).google?.maps) resolve((window as any).google);
        else reject(new Error("Google Maps loaded but global is missing"));
      };
      script.onerror = () => {
        script.remove();
        reject(new Error("Google Maps script failed to load"));
      };
      document.head.appendChild(script);
    });
  })
    .then((google) => {
      d.scriptInjection.finalStatus = "ok";
      d.scriptInjection.totalMs = Math.round(performance.now() - t0);
      publishDiagnostics(d);
      return google;
    })
    .catch((err) => {
      d.scriptInjection.finalStatus = "error";
      d.scriptInjection.totalMs = Math.round(performance.now() - t0);
      publishDiagnostics(d);
      throw err;
    });
}

// ---------- Public API ----------

export function loadGoogleMaps(): Promise<any> {
  if (typeof window !== "undefined" && (window as any).google?.maps?.places) {
    emitMapsLifecycle("loader", "instance:reuse", "google.maps already loaded");
    return Promise.resolve((window as any).google);
  }
  if (loadPromise) {
    emitMapsLifecycle("loader", "instance:reuse", "in-flight load promise");
    return loadPromise;
  }
  emitMapsLifecycle("loader", "instance:create", "starting load");

  const diagnostics = newDiagnostics();
  publishDiagnostics(diagnostics);
  const overallT0 = performance.now();
  console.debug(`${NS} starting load`, { startedAt: diagnostics.startedAt });

  loadPromise = (async () => {
    try {
      const apiKey = await fetchApiKey(diagnostics);
      const google = await injectScript(apiKey, diagnostics);
      diagnostics.status = "success";
      diagnostics.finishedAt = new Date().toISOString();
      diagnostics.totalMs = Math.round(performance.now() - overallT0);
      publishDiagnostics(diagnostics);
      console.info(
        `${NS} ✅ loaded in ${diagnostics.totalMs}ms`,
        {
          configMs: diagnostics.configEndpoint.totalMs,
          scriptMs: diagnostics.scriptInjection.totalMs,
          configAttempts: diagnostics.configEndpoint.attempts.length,
          scriptAttempts: diagnostics.scriptInjection.attempts.length,
        },
      );
      emitMapsLifecycle(
        "loader",
        "instance:create",
        `loaded in ${diagnostics.totalMs}ms (config ${diagnostics.configEndpoint.attempts.length}× / script ${diagnostics.scriptInjection.attempts.length}×)`,
      );
      return google;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      diagnostics.status = "error";
      diagnostics.finishedAt = new Date().toISOString();
      diagnostics.totalMs = Math.round(performance.now() - overallT0);
      diagnostics.error = message;
      publishDiagnostics(diagnostics);
      emitMapsLifecycle("loader", "error", message);
      console.error(
        `${NS} ❌ failed after ${diagnostics.totalMs}ms — ${message}`,
        diagnostics,
      );
      loadPromise = null;
      throw err instanceof Error ? err : new Error("Failed to load Google Maps");
    }
  })();

  return loadPromise;
}
